import { ReactNode, useEffect, useState } from "react";
import { Box, Collapse, Grid, Typography, Button, Tooltip } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useManageModal } from "../../../Manage/components/ManageModal";
import { useSlideInContext } from "../../../../context/SlideInContext";
import { useStudentContext } from "../../../../context/studentContext";
import usePersonAuthStore, { decodedToken } from "../../../store/personAuthStore"
import SendIcon from '@mui/icons-material/Send';
import EmailForm, { EmailFormProps, EmailFormSchema } from "./EmailForm";
import { useMutation } from "@apollo/client";
import { EditStudentParentEmailMutation, EditStudentParentEmailMutationVariables, NoteOriginType, Student, AddSessionNoteMutation, AddSessionNoteMutationVariables, NoteTypeEnum, AddStudentNoteMutation, AddStudentMutationVariables, AddStudentNoteMutationVariables } from "../../../../__generated__/graphql";
import { EDIT_STUDENT_PARENT_EMAIL } from "../../../../features/graphql/mutation/students/editStudentParentEmailMutation";
import { ADD_SESSION_NOTE_MUTATION, ADD_STUDENT_NOTE_MUTATION } from "../AddNote/AddNote";
import usePersonStore from "../../../store/personStore";
import EnlightnOptions from "./EnlightnOptions";
type PARENT_EMAIL_ACTION = "general" | "report1" | "report2" | "none";
type PREVIEW_EMAIL_INFO = "preview-general" | "preview-report1" | "preview-report2";
function LaunchButton({ action: type, currentAction: action, onAction, width = 180, disabled, children }: {
  action: PARENT_EMAIL_ACTION,
  currentAction: PARENT_EMAIL_ACTION | null,
  onAction: (action: PARENT_EMAIL_ACTION) => {}, children: ReactNode,
  width?: number,
  disabled?: boolean
}) {
  return (
    <LoadingButton
      sx={{
        backgroundColor: "primary.main",
        "&:hover": {
          backgroundColor: "primary.light",
        },
        width,
        marginTop: "16px",
        padding: 1
      }}
      type="submit"
      variant="contained"
      loading={type === action}
      loadingPosition="end"
      disabled={disabled}
      endIcon={<SendIcon />}
      onClick={() => onAction(type)}
    >
      {children}
    </LoadingButton>
  )
}

function EmailFormWrapper({ onSubmit }: EmailFormProps) {
  return (
    <Box>
      <Typography>There's no parent email for this child, please provide an email.</Typography>
      <EmailForm onSubmit={onSubmit} />
    </Box>
  )
}

export default function LaunchOptions() {
  const { openModal } = useManageModal();
  const { disableClosingDrawer, toggleDrawer } = useSlideInContext();
  const { studentData, updateStudent } = useStudentContext();
  const [showEnlightn, setShowEnlightn] = useState<boolean>(false)
  const [launchAction, setLaunchAction] = useState<PARENT_EMAIL_ACTION | null>(null);
  const [previewInfo, setPreviewInfo] = useState<PREVIEW_EMAIL_INFO | null>(null);
  const accessToken = usePersonAuthStore(state => state.access_token);
  const subscriptions = usePersonStore(state => state.company.subscriptions)
  const [addStudentNoteMutation] = useMutation<AddStudentNoteMutation, AddStudentNoteMutationVariables>(ADD_STUDENT_NOTE_MUTATION);

  const [editStudentParentEmail, { data }] =
    useMutation<EditStudentParentEmailMutation, EditStudentParentEmailMutationVariables>(EDIT_STUDENT_PARENT_EMAIL);

  const handleEmailSubmission = async (values: EmailFormSchema) => {
    const response = await editStudentParentEmail({
      variables: {
        id: studentData.id,
        parentEmail: values.email
      }
    });

    // Update the student (player) context object with the new parent email value
    // but do not overwrite exsiting values
    updateStudent({
      parentEmail: values.email
    } as Student);

    // console.error(response.errors?.at(0)?.message);
  }
  const handleLaunch = async (action: PREVIEW_EMAIL_INFO) => {
    // Prevent accidental closing of the application drawer
    // disableClosingDrawer(true);
    const link = document.createElement('a');

    setPreviewInfo(action);
    switch (action) {
      case "preview-general": {
        link.href = `${window.__env.API_URL}/api/file/download/generalParentInfo`
        break;
      }
      case "preview-report1": {
        const parentFiles = studentData.lastSession.report.filter((e: { category: string }) => e.category == "Parent-Report")
        //@ts-ignore
        const newestParentFile = parentFiles?.reduce((newest, current) => {
          return new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest;
        })
        link.href = `${window.__env.API_URL}/api/file/download/client/parent-reports/${newestParentFile.id}`
        break;
      }
      default: {
      }
    }
    link.target = "_blank"
    // Prevent potential memory leak and cleanup ObjectURL
    // resources that are no longer in use
    const linkHandler = () => {
      setTimeout(() => {
        link.removeEventListener('click', linkHandler);
        document.body.removeChild(link);
      }, 300);
    }

    link.addEventListener('click', linkHandler);
    document.body.appendChild(link);
    link.click()
  };

  const handleSend = async (action: PARENT_EMAIL_ACTION) => {
    const { pid, lid } = decodedToken(accessToken ?? '');
    setLaunchAction(action)
    switch (action) {
      case "general":
        var response = await fetch(`${window.__env.API_URL}/api/parent/send/general-information`, {
          method: "post",
          headers: {
            Pid: pid,
            Lid: lid,
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            personId: pid,
            studentId: studentData.id,
          }),
        })
        if (response.status >= 400) {
          showFailureMessage();
          disableClosingDrawer(false)
          setLaunchAction(null);
          return;
        }
        var res = await addStudentNoteMutation({
          variables: {
            studentId: studentData.id,
            type: NoteTypeEnum.ParentGeneralInformationSent,
            origin: NoteOriginType.ResultsTable,
            body: `Information Sent to parent: General Information`
          }
        })
        showSuccessMessage();
        toggleDrawer()

        disableClosingDrawer(false)
        setLaunchAction(null);
        break;
      case "report1":
        const parentFiles = studentData.lastSession.report.filter((e: { category: string }) => e.category == "Parent-Report")
        //@ts-ignore
        const newestParentFile = parentFiles?.reduce((newest, current) => {
          return new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest;
        })
        var response = await fetch(`${window.__env.API_URL}/api/parent/send/${newestParentFile.id}`, {
          method: "post",
          headers: {
            Pid: pid,
            Lid: lid,
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            personId: pid,
            studentId: studentData.id,
          }),
        })
        if (response.status >= 400) {
          showFailureMessage();
          disableClosingDrawer(false)
          setLaunchAction(null);
          return;
        }
        var res = await addStudentNoteMutation({
          variables: {
            studentId: studentData.id,
            type: NoteTypeEnum.ParentReportSent,
            origin: NoteOriginType.ResultsTable,
            body: `Information Sent to parent: Parent 1 Report`
          }
        })
        showSuccessMessage();
        toggleDrawer()

        disableClosingDrawer(false)
        setLaunchAction(null);
        break;
      case "report2":
      default: break;
    }

  }
  const showSuccessMessage = () => {
    toggleDrawer();
    disableClosingDrawer(false);
    openModal({ type: "PARENT_SEND_GENERAL_INFORMATION_SUCCESS", eventType: 'DISABLE_BACKDROP', cancelButtonLabel: "Close" });
  };

  const showFailureMessage = () => {
    toggleDrawer();
    disableClosingDrawer(false);
    openModal({ type: "GENERAL_ERROR", eventType: 'DISABLE_BACKDROP', cancelButtonLabel: "Close" });
  };
  useEffect(() => {
    console.log(studentData)
  }, [studentData])

  if (!studentData?.parentEmail)
    return <EmailFormWrapper onSubmit={handleEmailSubmission} />
  const subscriptionMessage = !subscriptions.some(sub => sub.name === "ENLIGHTN") ? "Not included in Subscription" : ""
  if (showEnlightn) {
    return (
      <EnlightnOptions />
    )
  }

  return (
    <Grid
      container
      spacing={1}
      direction="column"
      alignItems="center"
      sx={{ padding: "36px 16px", textAlign: "center" }}
    >
      <Grid size={12} direction="column" alignItems="center">
        <Typography sx={{ marginBottom: 2 }}>
          The Parent Email on file is <strong>{studentData.parentEmail}</strong>
        </Typography>
      </Grid>
      <Grid size={12} direction="column" alignItems="center">
        <Typography>
          What type of information would you like to send?
        </Typography>
        <Button sx={{
          marginTop: "16px",
          padding: 1
        }} variant="contained" onClick={() => handleLaunch("preview-general")}>
          View General Information
        </Button>
        <Collapse in={previewInfo === "preview-general"}>
          <Grid size={12} direction="column" alignItems="center">
            <LaunchButton
              action="general"
              currentAction={launchAction}
              width={210}
              onAction={handleSend}>Send General Information to parent</LaunchButton>
          </Grid>
        </Collapse>
        {/* We hide this so they can send info regardless of if they student has a complete session */}
        {studentData.lastSession ?
          <>
            <Button sx={{
              marginTop: "16px",
              padding: 1
            }} variant="contained" onClick={() => handleLaunch("preview-report1")}>
              View Parent 1 Report
            </Button>
            <Collapse in={previewInfo === "preview-report1"}>
              <Grid size={12} direction="column" alignItems="center">
                <LaunchButton
                  action="report1"
                  currentAction={launchAction}
                  width={210}
                  onAction={handleSend}>Send Parent Report to parent</LaunchButton>
              </Grid>
            </Collapse>
            <Button sx={{
              marginTop: "16px",
              padding: 1
            }} variant="contained" onClick={() => handleLaunch("preview-report2")} disabled>
              Coming Soon Parent 2 Report
            </Button>
            <Collapse in={previewInfo === "preview-report2"}>
              <Grid size={12} direction="column" alignItems="center">
                <LaunchButton
                  action="report2"
                  currentAction={launchAction}
                  width={210}
                  onAction={handleSend}>Send Parent Report to parent</LaunchButton>
              </Grid>
            </Collapse>
          </>
          :
          <></>}
      </Grid>
      <Grid size={12} direction="column" alignItems="center">
        <Tooltip title={subscriptionMessage}>
          <Box>
            <Button variant="contained" sx={{ marginTop: "16px", padding: 1 }}
              disabled={!subscriptions.some(sub => sub.name === "ENLIGHTN")}
              onClick={() => setShowEnlightn(true)}
            >
              View TrueSage Enlightn™ Materials
            </Button>
          </Box>
        </Tooltip>
      </Grid>
    </Grid>
  );
}


